import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { useEvent } from '../contexts/EventContext';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Car, ArrowLeft, Search, Grid, List, ArrowUpDown, Heart } from 'lucide-react';

export default function Gallery() {
  const { currentEvent } = useEvent();
  const [cars, setCars] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters and view options
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClass, setSelectedClass] = useState('all');
  const [sortBy, setSortBy] = useState('registration_number');
  const [sortDirection, setSortDirection] = useState('asc');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  useEffect(() => {
    if (currentEvent) {
      fetchCars();
      fetchClasses();
    }
  }, [currentEvent]);

  const fetchCars = async () => {
    if (!currentEvent) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/cars?event_id=${currentEvent.id}&approved_only=true`);
      setCars(response.data);
    } catch (error) {
      console.error('Error fetching cars:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    if (!currentEvent) return;
    try {
      const response = await axios.get(`${API}/classes?event_id=${currentEvent.id}`);
      setClasses(response.data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  // Filter and sort cars
  const filteredAndSortedCars = cars
    .filter(car => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          car.registration_number?.toLowerCase().includes(query) ||
          car.owner_name?.toLowerCase().includes(query) ||
          car.make?.toLowerCase().includes(query) ||
          car.model?.toLowerCase().includes(query) ||
          car.year?.toString().includes(query);
        if (!matchesSearch) return false;
      }
      
      // Class filter
      if (selectedClass !== 'all' && car.class_name !== selectedClass) {
        return false;
      }
      
      return true;
    })
    .sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'registration_number':
          aVal = parseInt(a.registration_number) || 0;
          bVal = parseInt(b.registration_number) || 0;
          break;
        case 'make_model':
          aVal = `${a.make} ${a.model}`.toLowerCase();
          bVal = `${b.make} ${b.model}`.toLowerCase();
          break;
        case 'class':
          aVal = a.class_name?.toLowerCase() || '';
          bVal = b.class_name?.toLowerCase() || '';
          break;
        case 'owner':
          aVal = a.owner_name?.toLowerCase() || '';
          bVal = b.owner_name?.toLowerCase() || '';
          break;
        default:
          aVal = a.registration_number;
          bVal = b.registration_number;
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

  const toggleSortDirection = () => {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
  };

  if (!currentEvent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/">
              <Button variant="ghost" className="text-gray-700 hover:text-pink-600 hover:bg-pink-50">
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back
              </Button>
            </Link>
            <Logo size="sm" showText={true} />
          </div>
          <div className="flex items-center space-x-4">
            <Link to="/vote">
              <Button className="bg-pink-600 hover:bg-pink-700 text-white">
                Vote
              </Button>
            </Link>
            <Link to="/leaderboard">
              <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50">
                Leaderboard
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 mt-8">
        {/* Title */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{currentEvent.name}</h1>
          <p className="text-gray-700">Car Gallery • {filteredAndSortedCars.length} vehicles</p>
        </div>

        {/* Filters Bar */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6 shadow-sm">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by registration #, owner, make, model..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-white border-gray-300 text-gray-900"
                />
              </div>
            </div>

            {/* Class Filter */}
            <div className="w-[250px]">
              <Select value={selectedClass} onValueChange={setSelectedClass}>
                <SelectTrigger className="bg-white border-gray-300 text-gray-900">
                  <SelectValue placeholder="Filter by class" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Classes</SelectItem>
                  {classes.map(cls => (
                    <SelectItem key={cls.id} value={cls.name}>{cls.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Sort By */}
            <div className="w-[180px]">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="bg-white border-gray-300 text-gray-900">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="registration_number">Reg #</SelectItem>
                  <SelectItem value="make_model">Make/Model</SelectItem>
                  <SelectItem value="class">Class</SelectItem>
                  <SelectItem value="owner">Owner</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Sort Direction */}
            <Button
              variant="outline"
              size="icon"
              onClick={toggleSortDirection}
              className="border-gray-300 text-gray-700 hover:bg-gray-50"
              title={sortDirection === 'asc' ? 'Ascending' : 'Descending'}
            >
              <ArrowUpDown className={`h-4 w-4 ${sortDirection === 'desc' ? 'rotate-180' : ''}`} />
            </Button>

            {/* View Mode Toggle */}
            <div className="flex border border-gray-300 rounded-md overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-pink-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                title="Grid view"
              >
                <Grid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-pink-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                title="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pink-500"></div>
          </div>
        ) : filteredAndSortedCars.length === 0 ? (
          <div className="text-center py-20 bg-white border border-gray-200 rounded-lg shadow-sm">
            <Car className="h-16 w-16 mx-auto mb-4 text-gray-400" />
            <p className="text-xl text-gray-700">
              {searchQuery || selectedClass !== 'all' ? 'No cars match your filters' : 'No cars registered yet'}
            </p>
            {(searchQuery || selectedClass !== 'all') && (
              <Button 
                variant="outline" 
                className="mt-4 border-gray-300 text-gray-700"
                onClick={() => { setSearchQuery(''); setSelectedClass('all'); }}
              >
                Clear Filters
              </Button>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          /* Grid View */
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAndSortedCars.map((car) => (
              <Link key={car.id} to={`/car/${car.id}`}>
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md hover:border-pink-300 transition-all cursor-pointer">
                  {car.photo_data ? (
                    <img
                      src={car.photo_data}
                      alt={`${car.make} ${car.model}`}
                      className="w-full h-48 object-cover"
                    />
                  ) : (
                    <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
                      <Car className="h-16 w-16 text-gray-300" />
                    </div>
                  )}
                  
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold px-3 py-1 rounded-full bg-pink-100 text-pink-600">
                        #{car.registration_number}
                      </span>
                      {car.checked_in && (
                        <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">
                          ✓ Checked In
                        </span>
                      )}
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-1">
                      {car.year} {car.make} {car.model}
                    </h3>
                    <p className="text-gray-600 text-sm mb-2">
                      {car.class_name}
                    </p>
                    <p className="text-sm text-gray-500">Owner: {car.owner_name}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          /* List View */
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Reg #</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Photo</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Vehicle</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Class</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Owner</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-gray-700">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredAndSortedCars.map((car) => (
                  <tr key={car.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <Link to={`/car/${car.id}`} className="text-pink-600 font-semibold hover:text-pink-700">
                        #{car.registration_number}
                      </Link>
                    </td>
                    <td className="px-6 py-4">
                      <Link to={`/car/${car.id}`}>
                        {car.photo_data ? (
                          <img
                            src={car.photo_data}
                            alt={`${car.make} ${car.model}`}
                            className="w-16 h-12 object-cover rounded"
                          />
                        ) : (
                          <div className="w-16 h-12 bg-gray-100 rounded flex items-center justify-center">
                            <Car className="h-6 w-6 text-gray-300" />
                          </div>
                        )}
                      </Link>
                    </td>
                    <td className="px-6 py-4">
                      <Link to={`/car/${car.id}`} className="hover:text-pink-600">
                        <span className="text-gray-900 font-medium">{car.year} {car.make} {car.model}</span>
                      </Link>
                    </td>
                    <td className="px-6 py-4 text-gray-700 text-sm">{car.class_name}</td>
                    <td className="px-6 py-4 text-gray-700">{car.owner_name}</td>
                    <td className="px-6 py-4 text-center">
                      {car.checked_in ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          ✓ Checked In
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
